import Keys from '../keys/keys.js';
import store from '../state/store.js';
import queryString from 'query-string';
import Uuid  from 'uuid/v4';
import {setUserNoCompany} from 'actions/user.js';

import {version, buildno} from '../../package.json';

const ErrorCodes = {
    InvalidSession:1003,
    NoCompany:1006
}

class DatabaseService {
    constructor(){
        this.setLoggedIn(localStorage.isLoggedIn);
        this.setPublic(localStorage.isPublicComputer);
        this.setInstallationId(localStorage.installationId);
        delete localStorage.token; // Deprecated: Only here to make sure old clients clear this variable
        this.handleHttpErrors = this.handleHttpErrors.bind(this);
    }

    setTranslator(i18n) {
        this.i18n = i18n;
    }

    getTranslator() {
        return this.i18n;
    }

    setPublic(isPublicComputer) {
        isPublicComputer = isPublicComputer == 'true' || isPublicComputer === true;
        localStorage.isPublicComputer = isPublicComputer;
        store.dispatch({type:'SET_USER_PUBLIC', isPublicComputer:isPublicComputer});
    }

    setLoggedIn(isLoggedIn) {
        isLoggedIn = isLoggedIn == 'true' || isLoggedIn === true;
        localStorage.isLoggedIn = isLoggedIn;
        store.dispatch({type:'SET_USER_LOGGED_IN', loggedIn:isLoggedIn});
    }

    setInstallationId(installationId) {
        if(!installationId || installationId.length < 10) {
            var uuid = Uuid();
            installationId = uuid + '-web';
        }
        localStorage.installationId = installationId;
    }

    setSurveysCache(surveys) {
        if(surveys && typeof surveys === 'object') {
            localStorage.surveysJSON = JSON.stringify(surveys);
        }
    }

    getSurveysCache() {
        return localStorage.surveysJSON ? JSON.parse(localStorage.surveysJSON) : {};
    }

    destroySession() {
        delete localStorage.surveysJSON;
        delete localStorage.isLoggedIn;
        delete localStorage.isPublicComputer;
        store.dispatch({type:'USER_SIGNOUT'}); //Clear redux store
    }

    signOut() {

        return this.httpJson('POST', '/logout', {} ) 
            .finally(() => {
                this.destroySession();
            });
    }

    signUp(username, password, code, isPublicComputer, profile) {
        return this.httpJson('POST', "/register", {username:username,password:password,code:code,profile:profile})
            .then(result => {
                this.setLoggedIn(true)
                this.setPublic(isPublicComputer)
                var noCompany = !(result.user.userProfile.companyId);
                store.dispatch(setUserNoCompany(noCompany));
                return result;
            });
    }

    changePassword(password, oldPassword) {
        return this.httpJson('PATCH', "/users", { user: {password:password}, password:oldPassword })
            .then(result => {
                return result.user;
            });
    }

    signIn(username, password, isPublicComputer) {
        return this.httpJson('POST', "/login", {username:username,password:password})
            .then(result => {
                this.setLoggedIn(true)
                this.setPublic(isPublicComputer)
                if(!(result.user.userProfile && result.user.userProfile.company)) {
                    store.dispatch(setUserNoCompany(true));
                }
                return result.user;
            });

    }

    switchCompany(code) {
        return this.httpJson('POST', "/profiles/me/company", {company:{code:code}})
            .then(result => {
                store.dispatch({type:'CLEAR_STORE'}); //Clear redux store
                this.setLoggedIn(true)
                return result.user;
            });
    }

    getCompanyInvitations() {
        return this.httpJson('GET', "/organizations", {registered:false, consumer:true});
    }

    requestPasswordReset(email) {
        return this.httpJson('PATCH', "/accounts", {email:email,password:{reset:true}});
    }

    sendAccountVerificationEmail() {
        return this.httpJson('PUT', "/profiles", {userProfile : {emailVerified:false} });
    }

    updateProfile(options) {
        return this.httpJson('PUT', "/profiles", options);// /profiles/me
    }

    resendVerificationEmail(options)
    {
        // TODO
        return this.http("dooo_v3_resendVerificationEmail", options)
    }

    fetchCompanyWelcome() {
        return this.httpJson('GET', "/welcome");
    }

    fetchBadgeNumbers() {
        return this.httpJson('GET', "/badge");
    }

    updateCompanyAnnouncementsBadgeDate(isoDateString) {
        //{"companyAnnouncementsBadgeDate":"2017-10-12T12:31:36.337-0400"}
        return this.httpJson('PUT', "/badge",{companyAnnouncementsBadgeDate:isoDateString});
    }

    fetchUserChallenge(userChallengeId)
    {
        return this.httpJson('GET', "/activities/" + userChallengeId, {userChallengeId:userChallengeId, historyLimit:7, categoryRank:true, calcDailyAverage:true, calcDailyAverageLimit:7});
    }
			
    fetchCategories()
    {
        return this.httpJson('GET', "/categories");
    }
			
    fetchCategorySections(categoryId)
    {
        return this.httpJson('GET', "/categories/"+categoryId+"/sections");
    }

    fetchObjectives()
    {
        return this.httpJson('GET', "/objectives");
    }

    userCategoryEnable(categoryId, enable)
    {
        var action = enable ? 'subscribe' : 'unsubscribe';
        return this.httpJson('POST', "/categories/" + categoryId + "/" + action, {categoryId : categoryId});
    }

    userCategoryUpdate(categoryId, params)
    {
        return this.httpJson('PATCH', "/categories/" + categoryId + "/notifications", {...params, categoryId : categoryId});
    }

    userCategoryUpdateLevel(categoryId, level)
    {
        return this.httpJson('POST', "/categories/" + categoryId, {level:level, subscribe:true});
    }

    batchCategorySubscribe(categoryIds)
    {
        return this.httpJson('POST', "/categories",{categoryIds:categoryIds, subscribe:true});
    }

    completeChallenge(challengeId, userChallenge, params)
    {
        return this.httpJson('POST', "/activities", {challengeId : challengeId, userChallenge:userChallenge, ...params});
    }

    completeActivity(challengeId, activity, params)
    {
        return this.httpJson('POST', "/activities", {challengeId : challengeId, activity : activity, ...params});
    }

    addActivityReaction(feedActionId, reactionId)
    {
        return this.httpJson('POST', "/actions/"+feedActionId+"/reaction", {challengeReactionId : reactionId});
    }

    removeActivityReaction(feedActionId, reactionId)
    {
        return this.httpJson('DELETE', "/actions/"+feedActionId+"/reaction", {reaction:{challengeReactionId : reactionId}});
    }

    reportActivityReaction(feedActionId, reactionId, comment)
    {
        return this.httpJson('POST', "/actions/"+feedActionId+"/report", {report:{challengeReportReasonId : reactionId,comment:comment}});
    }

    completeSurvey(surveyId, userChallenges) {
        return this.httpJson('POST', "/activities", {surveyId : surveyId, userChallenges:userChallenges});
    }

    surveyPageSavePointBefore(surveyId, pageId, userChallenges) {
        return this.httpJson('POST', "/surveys/"+surveyId+"/pages/" + pageId, {surveyId : surveyId, userChallenges:userChallenges});
    }

    surveyPageSavePointAfter(surveyId, pageId, userChallenges) {
        return this.httpJson('POST', "/surveys/"+surveyId+"/pages/" + pageId + "/pages", {surveyId : surveyId, userChallenges:userChallenges});
    }

    updateUserCategoryTimes(timesHash)
    {
        // TODO
        // Build hash of userCategoryId => userCategory.time pairs.
        return this.httpJson("updateUserCategoryTimes", {userCategories : timesHash})
    }

    getSurvey(surveyId, micro)
    {
        return this.httpJson('GET', "/surveys/"+surveyId,{micro:micro});
    }

    getSeason(seasonId, params)
    {
        return this.httpJson('GET', "/seasons/"+seasonId, params);
    }
			
    getSeasonCategories(seasonId, params)
    {
        return this.httpJson('GET', "/seasons/"+seasonId+"/categories", params);
    }
			
    // Timeline
    getTimeline(params)
    {
        return this.httpJson('GET', "/timeline",params);
    }

    skipChallenge(challengeId, forward)
    {
        return this.httpJson('DELETE', "/timeline/challenges/"+challengeId+'?increment='+(forward ? '1' : '-1'));
    }

    fetchChallenge(challengeId, params)
    {
        return this.httpJson('GET', "/challenges/" + challengeId, params);
    }

    fetchUserChallenges()
    {
        return this.httpJson('GET', "/activities");
    }

    fetchTeamChallengeMembers(params)
    {
        return this.httpJson('GET', "/records/challenge/members/me", params);
    }

    disbandTeamChallengeTeam(recordId) { 
        return this.httpJson('DELETE', '/records/challenge/'+recordId);
    }

    getTeamChallengeTeamInfo(recordId) {
        return this.httpJson('GET', '/records/challenge/'+recordId);
    }

    declineTeamChallengeInvitation(recordId) {
        return this.httpJson('PATCH', '/records/challenge/'+recordId+'/members/me',{member:{accepted:false}});
    }
    

    ////////////////////////////
    // Leaderboard Cloud Functions
    ////////////////////////////
    fetchLeaderboards()
    {
        return this.httpJson('GET', "/leaderboards");
    }

    fetchTeamChallengeLeaderboards() {
        return this.httpJson('GET', "/leaderboards",{type:'challenge', recordCounts:true});
    }

    fetchLeaderboard(params)
    {
        params = params || {};
        return this.httpJson('GET', "/leaderboards/" + params.leaderboardId);
    }

    fetchLeaderboardTeam(params)
    {
        params = params || {};
        return this.httpJson('GET', "/teams/" + params.teamId + "/leaderboards/" + params.leaderboardId);
    }

    fetchRecords(obj)
    {
        return this.httpJson('GET', "/records", obj);
    }
    
    // profile
    
    fetchProfiles(dataObj)
    {
        return this.httpJson('GET', "/profiles", dataObj);
    }

    fetchMyProfile(dataObj)
    {
        return this.httpJson('GET', "/profiles/me", dataObj).then(result => {
            this.setSurveysCache(result.surveys);
            return result;});
    }

    // reward
    
    fetchRewards()
    {
        return this.httpJson('GET', "/rewards", {});
    }

    redeemReward(rewardId)
    {
        return this.httpJson('POST', "/rewards/" + rewardId + "/redeem", {rewardId:rewardId});
    }

    // Newsfeed

    fetchCompanyAnnouncementsFeed(date, limit)
    {
        return this.httpJson('GET', "/announcements", {date:date, limit:limit});
    }

    fetchCompanyAnnouncement(id)
    {
        return this.httpJson('GET', "/announcements/"+id);
    }

    fetchActivityFeed(date, limit, userProfile)
    {
		var company = userProfile.company || {};
		var feedId = company.feedId;
        return this.httpJson('GET', "/feeds/company", {feedId:feedId, date:date, limit:limit, challengeReactions:true,challengeReportReasons:true});
    }

    fetchMyProgressFeed(date, limit, userProfile)
    {
		var feedId = userProfile.publicFeedId;
        return this.httpJson('GET', "/feeds/me", {feedId:feedId, date:date, limit:limit, challengeReactions:true,challengeReportReasons:true});
    }

    // plugins
    addUserPlugin(pluginId, data, clientKey)
    {
        return this.httpJson('POST', "/connections/"+pluginId+"/add", {pluginId:pluginId,data:data,clientKey:clientKey});
    }

    updateUserPlugin(pluginId, data, clientKey)
    {
        return this.httpJson('PUT', "/connections/"+pluginId+"/update", {pluginId:pluginId,data:data,clientKey:clientKey});
    }

    removeUserPlugin(pluginId)
    {
        return this.httpJson('DELETE', "/connections/"+pluginId+"/remove", {pluginId:pluginId});
    }

    fetchPluginSyncUrl(pluginId, redirectUri)
    {
        return this.httpJson('GET', "/connections/"+pluginId+"/sync", {pluginId:pluginId, redirectUri:redirectUri});
    }


    fetchPlugins(dataObj) {
        return this.httpJson("GET", "/connections", dataObj);
    }

    fetchLogCategories(dataObj) {
        return this.httpJson("GET", "/objectives/logs/categories", dataObj);
    }

    fetchLogCategory(id) {
        return this.httpJson("GET", "/categories/"+id, {challenges: true});
    }

    fetchInvitations(dataObj) {
        return this.httpJson("GET", "/invitations", dataObj);
    }

    inviteFriend(companyEmployeeId) {
        return this.httpJson("POST", "/invitations", {companyEmployeeId:companyEmployeeId});
    }

    testRoute() {
        return this.httpJson("POST", "/testRoute", {});
    }

    testContactsApi(params) {
        var contacts = [{
                "identifier": "057DDD38-08B8-401D-8630-8AE869299999",
                "givenName": "John",
                "familyName": "Doe",
                "organizationName": null,

                "phoneNumbers": [{
                "identifier": "1D47B48E-2F6B-485E-AB37-D7B436A99999",
                "label": "_$!<Work>!$_",
                "value": {
                "countryCode": "ca",
                "digits": "5551234567"
                }
                }],
                "emailAddresses": [{
                "identifier": "790BF7D9-D892-4AD8-9A8E-F2B0ABC99999",
                "label": "_$!<Work>!$_",
                "value": "j.doe@realcompeny.com"
                }],
                "postalAddresses": "(not fetched)"
                },
                {
                "identifier": "057DDD38-08B8-401D-8630-8AE869299989",
                "givenName": "Jane",
                "familyName": "Doe",
                "organizationName": null,

                "phoneNumbers": [{
                "identifier": "1D47B48E-2F6B-485E-AB37-D7B436A99989",
                "label": "_$!<Work>!$_",
                "value": {
                "countryCode": "ca",
                "digits": "5551234568"
                }
                }],
                "emailAddresses": [],
                "postalAddresses": "(not fetched)"
                }

                ];
        var defaultParams = {format:'android', contacts:contacts};
        params = params || defaultParams
        return this.httpJson("POST", "/contacts", params);
    }
    stressTestContactsApi() {
       
        var contacts = [];
        for(var i = 0; i < 1000; i++) {
            var contact = {
                    "identifier": "057DDD38-08B8-401D-8630-8AE869299999-"+i,
                    "givenName": "John"+i,
                    "familyName": "Doe"+i,
                    "organizationName": null,

                    "phoneNumbers": [{
                    "identifier": "1D47B48E-2F6B-485E-AB37-D7B436A99999-"+i,
                    "label": "_$!<Work>!$_",
                    "value": {
                    "countryCode": "ca",
                    "digits": "555-"+i
                    }
                    }],
                    "emailAddresses": [{
                    "identifier": "790BF7D9-D892-4AD8-9A8E-F2B0ABC99999-"+i,
                    "label": "_$!<Work>!$_",
                    "value": "j.doe+"+i+"+@realcompeny.com"
                    }],
                    "postalAddresses": "(not fetched)"
                }

            contacts.push(contact);
        }
        var params = {format:'android', contacts:contacts};
        return this.testContactsApi(params);
    }

    
    httpJson(args) {
        return this.http.apply(this, arguments).then(r => r.json());
    }


    http(method, url, dataObj, options) {
            options = options || {};

            var headers = {
                'X-Optimity-Platform': 'web',
                'X-Optimity-Appversion': version,
                'X-Optimity-Appbuild': buildno
            };

            var head = headers;

            if (localStorage.installationId) {
                head['X-Optimity-Installation-Id'] = localStorage.installationId;
            }

            if(localStorage.selectedLanguage) {
                head['Accept-Language'] = localStorage.selectedLanguage;
            }


            head["Content-Type"] = options.contentType || "application/json";

            if(!options.absoluteUrl) {
                var apiUrl = options.apiUrl || Keys.API_URL;
                url = apiUrl + url;
            }
            url = encodeURI(url); 
            var init = {
                credentials: "include",
                method: method,
                headers: head
            }
            if(method === 'GET') {
                if(dataObj!=null) {
                    var params = Object.keys(dataObj);
                    if(params.length > 0) {
                        url = url + '?' + queryString.stringify(dataObj);
                    }
                }
            }
            else {
                init.body = JSON.stringify(dataObj);
            }

            //console.log('fetch - url, init: ');
            //console.log(url);
            //console.log(init);
            return fetch(url, init).then(this.handleHttpErrors);
    }

    handleHttpErrors(response) {
        if (!response.ok) {
            console.error('handleHttpErrors', response);
            var statusText = response.statusText;
            var code = response.status;
            return new Promise((resolve,reject)=>{
                response.json().then((json)=>{
                    console.log('json:', json);
                    this.handleErrorCode(json.code);
                    reject(this.createHttpError(code, statusText, json));
                });
            });
        }
        return response;
    }

    handleErrorCode(code) {
        if(code == ErrorCodes.InvalidSession) {
            console.log('handleErrorCode => InvalidSession');
            this.destroySession();
            return true;
        } else if(code == ErrorCodes.NoCompany) {
            console.log('handleErrorCode => NoCompany');
            store.dispatch(setUserNoCompany(true));
        }
        return;
    }

    createHttpError(status, statusText, json) {

        console.log('createHttpError', json)

        var that = this;
        return ({
            ...json,
            status,
            statusText,
            //localizedError: that.i18n.translateErrorCode(json.code, status),
            toString:function(){
                return json.message;//this.localizedError;
            }
        });
    }

}


export default new DatabaseService();




// WEBPACK FOOTER //
// ./src/services/DatabaseService.js